import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';

const sourceTypeLabels: Record<string, string> = {
  WEBSITE: '홈페이지',
  NAVER_BLOG: '네이버 블로그',
  NAVER_CAFE: '네이버 카페',
  INSTAGRAM: '인스타그램',
  FACEBOOK: '페이스북',
  YOUTUBE: '유튜브',
  MANUAL: '수동 입력',
};

const ConditionList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [conditions, setConditions] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<any>(null);
  const [crawlOpen, setCrawlOpen] = useState(false);
  const [crawlLimit, setCrawlLimit] = useState<number | ''>(5);
  const [crawlRunning, setCrawlRunning] = useState(false);
  const [crawlResult, setCrawlResult] = useState<any>(null);

  useEffect(() => {
    loadConditions();
    loadStats();
  }, [page, rowsPerPage]);

  const loadConditions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: 'fishingDate',
        sortOrder: 'desc',
      };
      if (search) params.search = search;

      const data = await api.getConditions(params);
      setConditions(data.data);
      setTotal(data.total);
    } catch (e: any) {
      setError(e.message || '로딩 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.getConditionStats();
      setStats(data);
    } catch {}
  };

  const handleSearch = () => {
    setPage(0);
    loadConditions();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.deleteCondition(deleteId);
      setDeleteId(null);
      loadConditions();
      loadStats();
    } catch (e: any) {
      setError(e.message || '삭제 실패');
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      const data = await api.getCondition(id);
      setSelectedCondition(data);
      setDetailOpen(true);
    } catch {}
  };

  const handleTriggerCrawl = async () => {
    setCrawlRunning(true);
    setCrawlResult(null);
    try {
      const opts: any = {};
      if (crawlLimit !== '') opts.limit = Number(crawlLimit);
      const res = await api.triggerCrawl(opts);
      setCrawlResult(res);
    } catch (e: any) {
      setCrawlResult({ error: e.message || '요청 실패' });
    } finally {
      setCrawlRunning(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        조황 관리
      </Typography>

      {/* 통계 카드 */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="primary">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">전체</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="success.main">{stats.today}</Typography>
                <Typography variant="body2" color="text.secondary">오늘</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h4" color="info.main">{stats.thisWeek}</Typography>
                <Typography variant="body2" color="text.secondary">이번주</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                {stats.bySource?.map((s: any) => (
                  <Chip
                    key={s.sourceType}
                    label={`${sourceTypeLabels[s.sourceType] || s.sourceType}: ${s.count}`}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 검색 + 크롤 트리거 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            size="small"
            label="제목 또는 배 이름 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1, minWidth: { sm: 300 } }}
          />
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'space-between', sm: 'flex-end' }}>
            <Box>
              <IconButton onClick={handleSearch}><SearchIcon /></IconButton>
              <IconButton onClick={() => { setSearch(''); setPage(0); loadConditions(); }}><RefreshIcon /></IconButton>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { setCrawlResult(null); setCrawlOpen(true); }}
            >
              지금 크롤
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* 모바일: 카드 리스트 */}
      {isMobile && !isLoading && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {conditions.map((c) => (
            <Card key={c.id} sx={{ cursor: 'pointer' }} onClick={() => handleViewDetail(c.id)}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1.5}>
                  {c.images?.[0] ? (
                    <Box
                      component="img"
                      src={c.images[0].imageUrl}
                      sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                      onError={(e: any) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Box sx={{ width: 72, height: 72, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary">없음</Typography>
                    </Box>
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      #{c.id} · {c.boat?.name || '-'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, mt: 0.5,
                    }}>
                      {c.title}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Chip label={sourceTypeLabels[c.sourceType] || c.sourceType} size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(c.fishingDate)}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="column" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                    {c.sourceUrl && (
                      <IconButton size="small" component="a" href={c.sourceUrl} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => setDeleteId(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
          {conditions.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              조황 데이터가 없습니다
            </Typography>
          )}
        </Stack>
      )}

      {isMobile && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage=""
        />
      )}

      {/* 데스크탑: 테이블 */}
      {!isMobile && (
      <TableContainer component={Paper}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>이미지</TableCell>
                <TableCell>배 이름</TableCell>
                <TableCell>제목</TableCell>
                <TableCell>출처</TableCell>
                <TableCell>출조일</TableCell>
                <TableCell>등록일</TableCell>
                <TableCell align="right">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conditions.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  onClick={() => handleViewDetail(c.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    {c.images?.[0] ? (
                      <Box
                        component="img"
                        src={c.images[0].imageUrl}
                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                        onError={(e: any) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Box sx={{ width: 48, height: 48, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">없음</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{c.boat?.name || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.title}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sourceTypeLabels[c.sourceType] || c.sourceType}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(c.fishingDate)}</TableCell>
                  <TableCell>{formatDate(c.createdAt)}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => handleViewDetail(c.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    {c.sourceUrl && (
                      <IconButton size="small" component="a" href={c.sourceUrl} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => setDeleteId(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
      )}

      {/* 상세 다이얼로그 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>조황 상세</DialogTitle>
        <DialogContent dividers>
          {selectedCondition && (
            <Box>
              <Typography variant="h6" gutterBottom>{selectedCondition.title}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">배</Typography>
                <Typography variant="body2">{selectedCondition.boat?.name} ({selectedCondition.boat?.region})</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">출조일</Typography>
                <Typography variant="body2">{formatDate(selectedCondition.fishingDate)}</Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">출처</Typography>
                <Typography variant="body2">{sourceTypeLabels[selectedCondition.sourceType] || selectedCondition.sourceType}</Typography>
                {selectedCondition.species?.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold">어종</Typography>
                    <Typography variant="body2">{selectedCondition.species.join(', ')}</Typography>
                  </>
                )}
              </Box>
              {selectedCondition.sourceUrl && (
                <Link href={selectedCondition.sourceUrl} target="_blank" rel="noopener">
                  원본 페이지 열기
                </Link>
              )}
              {selectedCondition.images?.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedCondition.images.map((img: any) => (
                    <Box
                      key={img.id}
                      component="img"
                      src={img.imageUrl}
                      sx={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 1 }}
                      onError={(e: any) => { e.target.style.display = 'none'; }}
                    />
                  ))}
                </Box>
              )}
              {selectedCondition.content && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>내용 (조황 본문 원본)</Typography>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                      maxHeight: 600,
                      overflowY: 'auto',
                      '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
                      '& p': { my: 1 },
                      '& table': { borderCollapse: 'collapse', my: 1 },
                      '& td, & th': { border: '1px solid #ddd', p: 0.5 },
                    }}
                    // 크롤한 HTML 은 관리자만 보기 때문에 XSS 낮음. 필요시 sanitize 라이브러리 추가.
                    dangerouslySetInnerHTML={{ __html: selectedCondition.content }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>삭제 확인</DialogTitle>
        <DialogContent>이 조황을 삭제하시겠습니까?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>취소</Button>
          <Button color="error" onClick={handleDelete}>삭제</Button>
        </DialogActions>
      </Dialog>

      {/* 크롤 트리거 */}
      <Dialog open={crawlOpen} onClose={() => !crawlRunning && setCrawlOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>조황 크롤링 실행</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            낚시배 조황 페이지를 즉시 크롤합니다. 배당 약 5초 딜레이. 전체 335척 = 약 30분.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              type="number"
              size="small"
              label="배 수 제한 (limit)"
              value={crawlLimit}
              onChange={(e) => setCrawlLimit(e.target.value === '' ? '' : Number(e.target.value))}
              helperText="비우면 전체"
              disabled={crawlRunning}
              sx={{ width: 200 }}
            />
          </Box>
          {crawlResult && (
            <Alert
              severity={crawlResult.error ? 'error' : crawlResult.disabled ? 'warning' : 'success'}
              sx={{ mt: 2 }}
            >
              {crawlResult.error && `실패: ${crawlResult.error}`}
              {crawlResult.disabled && '이 서버에서는 크롤러가 비활성 (USE_FIRECRAWL_CRAWLER != true)'}
              {!crawlResult.error && !crawlResult.disabled && (
                <>
                  {crawlResult.message || '요청 접수됨'} — 진행상황은 서버 로그 참고.
                </>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrawlOpen(false)} disabled={crawlRunning}>닫기</Button>
          <Button
            variant="contained"
            onClick={handleTriggerCrawl}
            disabled={crawlRunning}
          >
            {crawlRunning ? '요청 중...' : '크롤 시작'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConditionList;
